import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, faqData, stats } from '@/mocks/comics';

export default function AboutPage() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://homekleans.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'http://homekleans.com/',
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'http://homekleans.com/',
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'http://homekleans.com',
    logo: `${'http://homekleans.com'}/logo.png`,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京市朝阳区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webpageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
      <Navbar />
      <main>
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb items={[{ name: '关于我们' }]} />

          <section className="mt-6 mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-black text-foreground-950 mb-4">
              关于叮当漫画网
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-3xl">
              {siteInfo.description}
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">品牌故事</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-lightbulb-line"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900">创立初心</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    叮当漫画网创立于2020年，创始团队由一群热爱漫画的资深读者组成。我们深知漫画爱好者对于高质量、无删减阅读体验的渴望，因此决心打造一个真正为读者服务的漫画平台。从最初的手工整理到如今拥有上万部漫画作品，叮当漫画网始终坚持"免费、完整、高清"的核心理念。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-shield-check-line"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900">品质承诺</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们承诺所有漫画内容均为无删减原版，让每一位读者都能体验作者最真实的创作意图。同时，我们采用高清画质呈现，确保每一格画面都清晰细腻，为您带来沉浸式的阅读享受。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-team-line"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900">用户至上</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    360万+注册用户的选择，99.2%的用户好评率。我们倾听每一位读者的声音，持续优化产品体验，致力于成为中文漫画阅读领域最值得信赖的品牌。
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary-50/60 via-background-50 to-accent-50/60 rounded-lg p-6 flex items-center">
                <div className="w-full">
                  <div className="grid grid-cols-3 gap-6 text-center">
                    {stats.map((stat, idx) => (
                      <div key={idx}>
                        <div className="text-3xl md:text-4xl font-black text-primary-500">{stat.value}{stat.suffix}</div>
                        <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-background-300/60">
                    <p className="text-sm text-foreground-600 text-center leading-relaxed">
                      自2020年成立以来，叮当漫画网已发展成为国内领先的免费漫画阅读平台之一。
                      我们始终坚持以用户体验为核心，不断优化产品功能，为漫画爱好者提供最优质的阅读服务。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '资深漫迷小李', text: '用叮当漫画三年了，从未让我失望！画质清晰、更新及时，最重要的是真的无删减。大黄丫头是我最近的心头好，推荐给大家！', rating: 5 },
                { name: '二次元少女小美', text: '界面简洁、体验流畅，下拉式阅读简直不要太方便。各种类型的漫画都有，每天都要来看看有没有更新。', rating: 5 },
                { name: '漫画博主阿强', text: '作为漫画评测博主，我用过几十个漫画平台。叮当漫画的无删减版体验确实是业界顶尖水平，值得推荐。', rating: 5 },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-secondary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <p className="text-xs font-semibold text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">常见问题</h2>
            <div className="space-y-4 max-w-3xl">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200 transition-colors duration-200">
                    <h4 className="text-sm font-semibold text-foreground-900 pr-8">{faq.question}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">商务合作</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-advertisement-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900">广告投放</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  叮当漫画网日均活跃用户超过50万，是漫画行业优质的品牌曝光渠道。我们提供开屏广告、信息流广告、专题合作等多种投放形式，助力品牌精准触达二次元核心用户群体。
                </p>
                <p className="text-sm text-foreground-500">联系邮箱：hello@homekleans.com</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-copyright-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900">版权合作</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  欢迎漫画作者、出版社、版权方与我们联系。我们提供正版授权漫画的线上分发、推广和运营服务，共同开拓中文漫画市场，实现互利共赢。
                </p>
                <p className="text-sm text-foreground-500">联系邮箱：hello@homekleans.com</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-links-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900">渠道合作</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们开放API接口和数据合作，欢迎阅读平台、内容聚合平台、手机厂商等合作伙伴洽谈流量互换、内容分发、联合运营等多元化合作模式。
                </p>
                <p className="text-sm text-foreground-500">联系邮箱：hello@homekleans.com</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-star-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900">作者入驻</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  欢迎原创漫画作者入驻叮当漫画网。我们为优秀创作者提供流量扶持、收益分成、版权保护等全方位支持，助您将优秀作品呈现给更多读者。
                </p>
                <p className="text-sm text-foreground-500">联系邮箱：hello@homekleans.com</p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h4>
                <p className="text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">客服电话</h4>
                <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">公司地址</h4>
                <p className="text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}